import { FC, useMemo } from 'react'

import { BellLineIcon, GridLineIcon, MagnifyerLineIcon } from '@inno-ui/icons'
import { BasicIconProps } from '@inno-ui/icons/lib/Icons.types'
import { useTranslation } from 'react-i18next'
import {
  APP_LIBRARY_TOUR_ID,
  NOTIFICATION_CENTER_TOUR_ID,
  UNIVERSAL_SEARCH_TOUR_ID
} from 'shared/hooks/useOnboarding'
import { useLaunchpadStore } from 'shared/stores/launchpadStore'
import { useNotificationStore } from 'shared/stores/notificationStore'
import {
  selectNotificationBellUnreadCount,
  useUnreadCountStore
} from 'shared/stores/unreadCountStore'
import { useCommonStore } from 'src/shared/stores/commonStore'

export interface ActionButtonConfig {
  Icon: FC<BasicIconProps>
  onClick: () => void
  tourId: string
  ariaLabel: string
  isActive?: boolean
  shortcut?: string[]
  unreadCount?: number
}

export const useTopDockActionButtons = (): ActionButtonConfig[] => {
  const { t } = useTranslation('translation')
  const setIsLaunchpadOpen = useLaunchpadStore(
    state => state.setIsLaunchpadOpen
  )
  const { notificationPaneState, toggleNotificationPane } =
    useNotificationStore()
  const setIsSearchOpen = useCommonStore(state => state.setIsSearchOpen)
  const notificationsUnreadCount = useUnreadCountStore(
    selectNotificationBellUnreadCount
  )

  return useMemo(
    () => [
      {
        Icon: GridLineIcon,
        onClick: () => setIsLaunchpadOpen(prev => !prev),
        tourId: APP_LIBRARY_TOUR_ID,
        ariaLabel: t('navbar.open_launchpad'),
        shortcut: ['alt', 'L']
      },
      {
        Icon: MagnifyerLineIcon,
        onClick: () => setIsSearchOpen(prev => !prev),
        tourId: UNIVERSAL_SEARCH_TOUR_ID,
        ariaLabel: t('navbar.search'),
        shortcut: ['meta', 'k']
      },
      {
        Icon: BellLineIcon,
        onClick: toggleNotificationPane,
        tourId: NOTIFICATION_CENTER_TOUR_ID,
        ariaLabel: t('navbar.notifications'),
        isActive: notificationPaneState === 'opened',
        unreadCount: notificationsUnreadCount,
        shortcut: ['alt', 'n']
      }
    ],
    [
      t,
      notificationPaneState,
      notificationsUnreadCount,
      setIsLaunchpadOpen,
      setIsSearchOpen,
      toggleNotificationPane
    ]
  )
}
